import React, { useState, useEffect } from "react";
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { config } from "../../../constants";
import Input from "../../controls/Input";
// @ts-ignore
import ButtonSpinner from "../../controls/ButtonSpinner";

interface UploadedMedia {
  id: number;
  is_new?: boolean;
  imageName?: string;
  media: string;
  title: string;
}

interface ImageGalleryModalProps {
  images: UploadedMedia[];
  onTitleChange?: (index: number, newTitle: string) => void;
  onSaveTitle?: (imageId: number, title: string) => Promise<any>;
  isSaving?: boolean;
  setOpen?: (modalState: { open: boolean; childComponent: any }) => void;
  initialIndex?: number;
}

export default function ImageGalleryModal({ images, onTitleChange, onSaveTitle, isSaving, setOpen, initialIndex = 0 }: ImageGalleryModalProps) {
  // Ensure the initial index is within bounds
  const safeInitialIndex = initialIndex >= 0 && initialIndex < images.length ? initialIndex : 0;
  const [currentIndex, setCurrentIndex] = useState(safeInitialIndex);
  const [editableTitles, setEditableTitles] = useState<string[]>(
    images.map(img => img.title)
  );

  // Update currentIndex when initialIndex changes (e.g., when opening a different image)
  useEffect(() => {
    const safeIndex = initialIndex >= 0 && initialIndex < images.length ? initialIndex : 0;
    setCurrentIndex(safeIndex);
  }, [initialIndex, images.length]);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = event.target.value;
    const newTitles = [...editableTitles];
    newTitles[currentIndex] = newTitle;
    setEditableTitles(newTitles);
    
    // Call the optional callback to notify parent component
    if (onTitleChange) {
      onTitleChange(currentIndex, newTitle);
    }
  };

  const handleSave = async () => {
    if (onSaveTitle) {
      const currentImage = images[currentIndex];
      const result = await onSaveTitle(currentImage.id, editableTitles[currentIndex]);
      
      // Close modal on successful save
      if (result && result.success && setOpen) {
        setOpen({ open: false, childComponent: null });
      }
    }
  };

  return (
    <div className="justify-between relative">
      <div className="py-1 gap-4 bg-white sm:rounded-xl space-y-4">
        {/* Close button */}
        {setOpen && (
          <button
            onClick={() => setOpen({ open: false, childComponent: null })}
            className="absolute top-4 right-4 z-10 bg-gray-100 hover:bg-gray-200 rounded-full p-2 shadow-sm transition-colors"
            title="Close"
          >
            <XMarkIcon className="w-5 h-5 text-gray-600" />
          </button>
        )}
        <div>
          <h1 className="text-center text-2xl font-bold mt-6">Image Gallery</h1>
          <div className="relative w-full max-w-xl mx-auto mt-6">
            <img
              src={
                images[currentIndex].is_new
                  ? images[currentIndex].media
                  : config.url.STORAGE_URL + images[currentIndex].media
              }
              alt={`image-${currentIndex}`}
              className="w-full object-cover rounded-md shadow-md"
              width="100%"
              height="100%"
            />
            <div className="pt-4 px-4 text-center">
              <Input
                name={`image-title-${currentIndex}`}
                value={editableTitles[currentIndex]}
                onChange={handleTitleChange}
                className="w-full py-2 text-center text-xl border-none bg-transparent focus:ring-0 focus:border-none"
                placeholder="Enter image title..."
              />
              <div className="mt-4">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isSaving}
                  className="inline-flex justify-center rounded-md bg-main-purple px-4 py-2 text-md font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ButtonSpinner spin={isSaving} text="Save Title" />
                </button>
              </div>
            </div>

            <button
              onClick={goToPrevious}
              className="absolute top-1/2 left-2 transform -translate-y-1/2 bg-white/70 hover:bg-white p-2 rounded-full shadow"
              title="Previous"
            >
              <ChevronLeftIcon className="w-6 h-6" />
            </button>

            <button
              onClick={goToNext}
              className="absolute top-1/2 right-2 transform -translate-y-1/2 bg-white/70 hover:bg-white p-2 rounded-full shadow"
              title="Next"
            >
              <ChevronRightIcon className="w-6 h-6" />
            </button>

            <div className="text-center mt-2 text-lg text-gray-600">
              {currentIndex + 1} / {images.length}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
